import { UTCDate } from '@date-fns/utc';
import { endOfDay, startOfDay, subMonths, subYears } from 'date-fns';
import { useMemo, useState } from 'react';
import Chart from 'react-google-charts';
import { Button, Checkbox, Icon, Label } from 'semantic-ui-react';
import Loader from '../../../../../components/loader';
import AuthService from '../../../../../service/auth';
import QuantityPerformanceConfig from '../productivity/components/quantityPerformanceConfig';
import {
    All_PROPS,
    userIDsWithKPIsChartReadPermissions,
    DEFAULT_CART_CFG_OPS,
    INTERVAL_TYPE,
    StatisticProps,
} from './constants';
import './styles.css';
import { createStatisticsArray, getIntervals, useActivity, useProductivity, getChartData } from './utils';
import useUsersMap from '../../../../../hooks/useUsersMap';
import { ROLES } from '../../../../../constnats/user';

const END_OF_TODAY = endOfDay(new UTCDate());

const INTERVAL_DEFAULT_SPEC = {
    [INTERVAL_TYPE.WEEKLY]: {
        startDateUTC: startOfDay(subMonths(END_OF_TODAY, 1)),
        endDateUTC: END_OF_TODAY,
        intervalType: INTERVAL_TYPE.WEEKLY,
    },
    [INTERVAL_TYPE.MONTHLY]: {
        startDateUTC: startOfDay(subYears(END_OF_TODAY, 1)),
        endDateUTC: END_OF_TODAY,
        intervalType: INTERVAL_TYPE.MONTHLY,
    },
};

function getIntervalOption(frameInMonths) {
    return {
        label: 'Past ' + frameInMonths + ' months',
        getIntervalSpec: (endDateUTC) => {
            return {
                startDateUTC: startOfDay(subMonths(endDateUTC, frameInMonths)),
            };
        },
    };
}

const INTERVAL_TIME_FRAME_OPTIONS = {
    [INTERVAL_TYPE.WEEKLY]: [getIntervalOption(1), getIntervalOption(6), getIntervalOption(13)],
    [INTERVAL_TYPE.MONTHLY]: [getIntervalOption(13), getIntervalOption(25)],
};

function KpisChart() {
    const { usersMap } = useUsersMap({ includeDisabled: true, userRoles: [ROLES.EMPLOYEE] });
    const [intervalSpec, setIntervalSpec] = useState({
        ...INTERVAL_DEFAULT_SPEC[INTERVAL_TYPE.WEEKLY],
    });

    const intervals = useMemo(
        () => getIntervals(intervalSpec.startDateUTC, intervalSpec.endDateUTC, intervalSpec.intervalType),
        [intervalSpec]
    );

    const [productivity, loadingProductivity, allTableTypes, config, setConfig] = useProductivity(usersMap, intervals);
    const [activity, loadingActivity] = useActivity(intervals);
    const [propsToPlotOnChart, setPropsToPlotOnChart] = useState([
        StatisticProps.totalScoreToActualWorkingHoursRatio.propName,
    ]);

    const handlePropToPlotOnChartToggle = (prop) => {
        setPropsToPlotOnChart((prev) => {
            if (prev.includes(prop)) {
                return prev.filter((x) => x !== prop);
            } else {
                return [...prev, prop];
            }
        });
    };

    const handleIntervalSpecChange = () => {
        setIntervalSpec((prev) => {
            const newMode = prev.intervalType === INTERVAL_TYPE.WEEKLY ? INTERVAL_TYPE.MONTHLY : INTERVAL_TYPE.WEEKLY;
            let newIntervalSpec = { ...prev };
            if (prev.intervalType !== newMode) {
                newIntervalSpec = { ...INTERVAL_DEFAULT_SPEC[newMode] };
            }
            return newIntervalSpec;
        });
    };

    if (!userIDsWithKPIsChartReadPermissions.includes(AuthService.getUserId())) {
        return <>Not enough privileges!</>;
    }

    return (
        <>
            <QuantityPerformanceConfig config={config} tableTypes={allTableTypes} onChange={setConfig} />
            <div className="kpis-chart__chart-container">
                <table className="kpis-chart__props-table">
                    <tbody>
                        {All_PROPS.map((prop) => {
                            const isPropIncluded = propsToPlotOnChart.includes(prop.propName);
                            const iconName = isPropIncluded ? 'checkmark' : 'x';
                            const iconColor = isPropIncluded ? 'green' : 'red';
                            return (
                                <tr
                                    key={prop.propName}
                                    onClick={() => handlePropToPlotOnChartToggle(prop.propName)}
                                    className="cursorPointer"
                                >
                                    <td>{<Icon size="tiny" name={iconName} color={iconColor} />}</td>
                                    <td>{prop.label}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="kpis-chart__mode-switch">
                    <Label>Monthly</Label>
                    <Checkbox
                        onChange={handleIntervalSpecChange}
                        checked={intervalSpec.intervalType === INTERVAL_TYPE.WEEKLY}
                        slider
                    />
                    <Label>Weekly</Label>
                </div>
                <div className="kpi-chart__chart">
                    {loadingProductivity || loadingActivity ? (
                        <Loader />
                    ) : (
                        <ChartComponent
                            activity={activity}
                            productivity={productivity}
                            propsToPlotOnChart={propsToPlotOnChart}
                        />
                    )}
                    <IntervalOptions
                        setIntervalSpec={setIntervalSpec}
                        options={INTERVAL_TIME_FRAME_OPTIONS[intervalSpec.intervalType]}
                    />
                </div>
            </div>
        </>
    );
}

function ChartComponent({ productivity, activity, propsToPlotOnChart }) {
    const combinedStats = createStatisticsArray(productivity, activity);
    const chartData = getChartData(combinedStats, propsToPlotOnChart);
    return <Chart chartType="LineChart" width="100%" height="450px" data={chartData} options={DEFAULT_CART_CFG_OPS} />;
}

function IntervalOptions({ options, setIntervalSpec }) {
    const handleClick = (option) => {
        setIntervalSpec((prev) => {
            const { startDateUTC } = option.getIntervalSpec(prev.endDateUTC);
            return {
                ...prev,
                startDateUTC,
            };
        });
    };

    return (
        <div className="kpis-chart__interval-options">
            {options.map((option) => (
                <Button key={option.label} onClick={() => handleClick(option)}>
                    {option.label}
                </Button>
            ))}
        </div>
    );
}

export default KpisChart;
